// MeetRoomSettingsModal — owner-only modal for editing an existing room's
// guest-join requirements + privacy flag + name. Opens from the SettingsBtn
// on MeetControlBar (or from the list Detail — wherever we surface it).
//
// Draft is buffered into root.roomSettingsDraft so toggles feel immediate
// without write-on-every-click churn. Save button fires updateRoomSettings
// which hits meet_rooms via RLS. Cancel discards the draft.
//
// Invite link section shows the /meet-guest URL for public rooms and a
// note for private rooms explaining that guests can't join.

// Pull the currently-edited room out of state. When the modal opens we
// seed the draft from this room's current values; reactive getters on
// toggles read the draft (so user changes reflect instantly) and fall
// back to the room value if the draft hasn't been seeded yet.
const _editRoom = (root) =>
  (root?.rooms || []).find((r) => r.id === root?.activeRoomId) || null

const _draftValue = (root, key) => {
  const draft = root?.roomSettingsDraft
  if (draft && key in draft) return draft[key]
  return _editRoom(root)?.[key]
}

const _patchDraft = (root, patch) => {
  const current = root?.roomSettingsDraft || {}
  root?.update({ roomSettingsDraft: { ...current, ...patch } })
}

// Toggle row — shared shape for each boolean setting. We define it as a
// plain object factory (called at declaration time) rather than a
// child-extends so every field can pass its own label + key statically.
const _toggleField = (key, title, caption) => ({
  flow: 'x',
  align: 'center space-between',
  gap: 'Z1',
  padding: 'Z 0',

  Label: {
    flow: 'y',
    gap: 'V',
    flex: '1',

    Title: { tag: 'span', fontSize: 'Z2', color: 'title', fontWeight: '500', text: title },
    Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: caption }
  },

  Toggle: {
    tag: 'button',
    padding: 'V Z',
    round: 'Z',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'Z2',
    fontWeight: '600',
    theme: (el, s) => _draftValue(s.root, key) ? 'primary' : 'secondary',
    text: (el, s) => _draftValue(s.root, key) ? 'On' : 'Off',
    onClick: (e, el, s) => _patchDraft(s.root, { [key]: !_draftValue(s.root, key) })
  }
})

export const MeetRoomSettingsModal = {
  extends: 'Modal',
  stateKey: 'showRoomSettings',
  extraClose: { roomSettingsDraft: null },

  // Seed the draft from the room's current values on open. Modal's display
  // is driven by the stateKey, so onCreate fires once when the element
  // mounts; we check whether the draft is already set and initialize it.
  // The draft is cleared by extraClose when the modal closes.
  onUpdate: (el, s) => {
    if (!s.root?.showRoomSettings) return
    if (s.root?.roomSettingsDraft) return
    const room = _editRoom(s.root)
    if (!room) return
    s.root?.update({
      roomSettingsDraft: {
        name: room.name || '',
        isPrivate: !!room.isPrivate,
        guestRequireName: room.guestRequireName !== false,
        guestRequireEmail: !!room.guestRequireEmail,
        guestAutoAdmit: !!room.guestAutoAdmit,
        guestPassword: room.guestPassword || ''
      }
    })
  },

  Window: {
    width: 'I',
    maxWidth: '90vw',
    gap: 'A',
    ModalHeader: {
      Hgroup: {
        H: { text: 'Room settings' },
        P: { text: 'Configure guest join requirements, name, and privacy. Changes apply immediately for new joiners.' }
      }
    },

    NameField: {
      flow: 'y',
      gap: 'V',

      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'NAME' },
      Input: {
        tag: 'input',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'document',
        color: 'title',
        fontSize: 'Z2',
        placeholder: 'e.g. Weekly product sync',
        value: (el, s) => _draftValue(s.root, 'name') || '',
        onInput: (e, el, s) => _patchDraft(s.root, { name: e.target.value })
      }
    },

    PrivateField: _toggleField(
      'isPrivate',
      'Private',
      'Only members can join. No guest link, no waiting room.'
    ),

    // Guest requirement toggles. Only visible when the room is public —
    // they're meaningless for private rooms where no one outside the
    // member list can get in regardless.
    GuestSection: {
      hide: (el, s) => !!_draftValue(s.root, 'isPrivate'),
      flow: 'y',
      gap: 'Z',
      padding: 'A 0 0 0',
      borderTopWidth: '1px',
      borderTopColor: 'line',
      borderTopStyle: 'solid',

      SectionTitle: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '600',
        letterSpacing: '.07em',
        color: 'caption',
        text: 'GUEST JOIN'
      },

      RequireName: _toggleField(
        'guestRequireName',
        'Require name',
        'Ask guests to enter a name before they can request to join.'
      ),

      RequireEmail: _toggleField(
        'guestRequireEmail',
        'Require email',
        'Ask guests to enter an email too. Stored with the waiting-room request.'
      ),

      AutoAdmit: _toggleField(
        'guestAutoAdmit',
        'Auto-admit',
        'Skip the waiting room — anyone who fills the form joins immediately. Use for low-sensitivity public calls.'
      ),

      GuestPasswordField: {
        flow: 'y',
        gap: 'V',
        padding: 'V 0',

        Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'PASSWORD (optional)' },
        Input: {
          tag: 'input',
          type: 'text',
          autocomplete: 'off',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          placeholder: 'Leave blank for no password',
          value: (el, s) => _draftValue(s.root, 'guestPassword') || '',
          onInput: (e, el, s) => _patchDraft(s.root, { guestPassword: e.target.value })
        },
        Hint: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: 'Guests must type this before the waiting-room request is accepted.'
        }
      },

      InviteLinkField: {
        flow: 'y',
        gap: 'V',
        padding: 'Z 0',

        Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'INVITE LINK' },
        Row: {
          flow: 'x',
          gap: 'V',
          align: 'center left',

          Link: {
            tag: 'input',
            flex: '1',
            padding: 'Z A',
            round: 'Z',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',
            background: 'overlay',
            color: 'title',
            fontSize: 'Y2',
            readonly: 'true',
            value: (el, s) => {
              const id = s.root?.activeRoomId
              if (!id || typeof window === 'undefined') return ''
              return `${window.location.origin}/meet-guest#room=${id}`
            },
            onClick: (e) => { e.target.select() }
          },
          CopyBtn: {
            tag: 'button',
            padding: 'Z A',
            round: 'Z',
            border: 'none',
            theme: 'secondary',
            cursor: 'pointer',
            fontSize: 'Z2',
            fontWeight: '600',
            text: (el, s) => s.root?.inviteLinkCopied ? 'Copied' : 'Copy',
            onClick: async (e, el, s) => {
              const id = s.root?.activeRoomId
              if (!id || typeof window === 'undefined') return
              const url = `${window.location.origin}/meet-guest#room=${id}`
              try {
                await navigator.clipboard.writeText(url)
                s.root?.update({ inviteLinkCopied: true })
                setTimeout(() => s.root?.update({ inviteLinkCopied: false }), 1500)
              } catch (err) {
                console.warn('[MeetRoomSettingsModal] clipboard write failed', err)
              }
            }
          }
        }
      }
    },

    PrivateNotice: {
      hide: (el, s) => !_draftValue(s.root, 'isPrivate'),
      padding: 'Z A',
      round: 'Z',
      background: 'overlay',
      color: 'caption',
      fontSize: 'Y2',
      text: 'Private rooms don\'t accept guest joins. Add members explicitly instead.'
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',

      SaveBtn: {
        extends: 'Button',
        theme: 'primary',
        text: 'Save',
        onClick: async (e, el, s) => {
          const draft = s.root?.roomSettingsDraft
          if (!draft) return
          const ok = await el.call('updateRoomSettings', {
            roomId: s.root?.activeRoomId,
            name: (draft.name || '').trim() || undefined,
            isPrivate: draft.isPrivate,
            guestRequireName: draft.guestRequireName,
            guestRequireEmail: draft.guestRequireEmail,
            guestAutoAdmit: draft.guestAutoAdmit,
            guestPassword: (draft.guestPassword || '').trim()
          })
          if (ok) s.root?.update({ showRoomSettings: false, roomSettingsDraft: null })
        }
      }
    }
  }
}
